<template>
  <v-card class="operator-tracking-map" elevation="2">
    <v-card-title class="d-flex align-center">
      <v-icon class="mr-2">mdi-map-marker-multiple</v-icon>
      Tracking de Operadores en Tiempo Real
      <v-spacer />
      <v-chip
        :color="Object.keys(operatorPositions).length > 0 ? 'success' : 'grey'"
        size="small"
        class="mr-2"
      >
        {{ Object.keys(operatorPositions).length }} operadores activos
      </v-chip>
      <v-btn
        icon
        size="small"
        @click="fitAllOperators"
        :disabled="Object.keys(operatorPositions).length === 0"
      >
        <v-icon>mdi-fit-to-screen</v-icon>
        <v-tooltip activator="parent" location="bottom">Ajustar vista</v-tooltip>
      </v-btn>
    </v-card-title>

    <v-card-text class="pa-0">
      <div
        ref="mapContainer"
        :style="{ height: mapHeight, width: '100%' }"
        class="tracking-map-container"
      />

      <!-- P13-V8 (DEC-V8-04): the route SLA alert banner is retired with the route SLA producer. -->
    </v-card-text>
  </v-card>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, watch, nextTick } from 'vue'

declare const L: any

interface OperatorPosition {
  operatorId: number
  latitude: number
  longitude: number
  accuracy: number | null
  speed: number | null
  heading: number | null
  timestamp: string
  executionId?: number
}

const props = withDefaults(defineProps<{
  mapHeight?: string
}>(), {
  mapHeight: '450px'
})

const mapContainer = ref<HTMLElement | null>(null)
let map: any = null
const operatorMarkers = new Map<number, any>()
const operatorPositions = ref<Record<number, OperatorPosition>>({})

// Leaflet icon for operators
function createOperatorIcon(hasAlert = false) {
  if (typeof L === 'undefined') return null
  return L.divIcon({
    html: `<div style="
      background: ${hasAlert ? '#ff5252' : '#1976d2'};
      border: 2px solid white;
      border-radius: 50%;
      width: 14px;
      height: 14px;
      box-shadow: 0 2px 6px rgba(0,0,0,0.3);
    "></div>`,
    className: 'operator-tracking-icon',
    iconSize: [18, 18],
    iconAnchor: [9, 9]
  })
}

function initMap() {
  if (!mapContainer.value || typeof L === 'undefined') return

  // Default center: Santo Domingo, RD
  map = L.map(mapContainer.value, {
    center: [18.4861, -69.9312],
    zoom: 10,
    zoomControl: true
  })

  L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
    attribution: '&copy; OpenStreetMap',
    maxZoom: 19
  }).addTo(map)
}

function updateOperatorMarker(pos: OperatorPosition) {
  if (!map) return

  operatorPositions.value[pos.operatorId] = pos

  const icon = createOperatorIcon(false)

  if (operatorMarkers.has(pos.operatorId)) {
    const marker = operatorMarkers.get(pos.operatorId)
    marker.setLatLng([pos.latitude, pos.longitude])
    if (icon) marker.setIcon(icon)
    marker.setPopupContent(buildPopup(pos))
  } else {
    const marker = L.marker([pos.latitude, pos.longitude], { icon })
      .addTo(map)
      .bindPopup(buildPopup(pos))
    operatorMarkers.set(pos.operatorId, marker)
  }
}

function buildPopup(pos: OperatorPosition): string {
  const time = new Date(pos.timestamp).toLocaleTimeString('es-DO')
  const acc = pos.accuracy ? `${Math.round(pos.accuracy)}m` : 'N/A'
  const spd = pos.speed != null ? `${(pos.speed * 3.6).toFixed(1)} km/h` : 'N/A'
  return `
    <div style="min-width:150px">
      <strong>Operador #${pos.operatorId}</strong><br>
      <small>Hora: ${time}</small><br>
      <small>Precisión: ${acc}</small><br>
      <small>Velocidad: ${spd}</small>
    </div>
  `
}

function fitAllOperators() {
  if (!map || operatorMarkers.size === 0) return
  const bounds = L.latLngBounds(
    Array.from(operatorMarkers.values()).map((m: any) => m.getLatLng())
  )
  map.fitBounds(bounds, { padding: [30, 30] })
}

// --- WebSocket handlers ---
function handleGpsUpdate(data: any) {
  if (!data?.operatorId || !data?.latitude || !data?.longitude) return
  updateOperatorMarker({
    operatorId: data.operatorId,
    latitude: data.latitude,
    longitude: data.longitude,
    accuracy: data.accuracy ?? null,
    speed: data.speed ?? null,
    heading: data.heading ?? null,
    timestamp: data.timestamp || new Date().toISOString(),
    executionId: data.executionId
  })
}

onMounted(async () => {
  await nextTick()
  initMap()
})

onUnmounted(() => {
  if (map) {
    map.remove()
    map = null
  }
  operatorMarkers.clear()
})
</script>

<style scoped>
.operator-tracking-map {
  overflow: hidden;
}

.tracking-map-container {
  min-height: 300px;
}

:deep(.operator-tracking-icon) {
  background: transparent !important;
  border: none !important;
}
</style>
